<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { formatDistanceToNow } from 'date-fns';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  ownerName: { type: String, default: '' },
  contactName: { type: String, default: '' },
  usersCount: { type: Number, default: 0 },
  updatedAt: { type: [String, Number], default: null },
});

const { t } = useI18n();
const router = useRouter();

const onClickViewDetails = event => {
  event?.stopPropagation?.();
  router.push({
    name: 'supplier_details',
    params: {
      supplierId: props.id,
    },
  });
};

const displayName = computed(() => props.name || t('SUPPLIERS.UNNAMED'));

const formattedUpdatedAt = computed(() => {
  if (!props.updatedAt) return '';
  return formatDistanceToNow(new Date(props.updatedAt), { addSuffix: true });
});
</script>

<template>
  <CardLayout layout="row" @click="onClickViewDetails">
    <div class="flex items-center justify-start flex-1 gap-4">
      <Avatar
        :username="displayName"
        class="shrink-0"
        :name="displayName"
        :size="48"
        hide-offline-status
        rounded-full
      />
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <span class="text-base font-medium truncate text-n-slate-12">
          {{ displayName }}
        </span>
        <div class="flex items-center justify-between gap-3">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <span v-if="ownerName" class="text-sm text-n-slate-11 truncate">
              {{ t('SUPPLIERS.OWNER_LABEL', { name: ownerName }) }}
            </span>
            <span v-if="contactName" class="text-sm text-n-slate-11 truncate">
              {{ t('SUPPLIERS.CONTACT_LABEL', { name: contactName }) }}
            </span>
            <span class="text-sm text-n-slate-11 truncate">
              {{ t('SUPPLIERS.USERS_COUNT', { n: usersCount }) }}
            </span>
            <div
              v-if="ownerName || contactName || usersCount"
              class="w-px h-3 bg-n-slate-6"
            />
            <Button
              :label="t('SUPPLIERS.CARD.VIEW_DETAILS')"
              variant="link"
              size="xs"
              @click="onClickViewDetails"
            />
          </div>
          <span
            v-if="updatedAt"
            class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 flex-shrink-0"
          >
            {{ formattedUpdatedAt }}
          </span>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
